import type { DailyBonusResponse, DailyQuestResponse, Wallet, WeeklyGiftResponse, WorldOfferResponse } from '@goodtime/shared';
import { api } from './client';

/** {@code GET /v1/players/{id}/wallet}: the server's wallet. */
export function fetchWallet(playerId: string): Promise<Wallet> {
  return api<Wallet>(`/players/${encodeURIComponent(playerId)}/wallet`);
}

/** {@code POST /v1/players/{id}/wallet/daily-bonus}: claim today's daily
 *  bonus on the server wallet. Safe to retry: a second claim inside 24 hours
 *  credits nothing and returns {@code claimed: false}. */
export function claimDailyBonus(playerId: string): Promise<DailyBonusResponse> {
  return api<DailyBonusResponse>(`/players/${encodeURIComponent(playerId)}/wallet/daily-bonus`, {
    method: 'POST',
  });
}

/** {@code POST /v1/players/{id}/wallet/daily-quest}: claim the world map's
 *  daily quest (coins + a gem) on the server wallet. Safe to retry: a second
 *  claim inside 24 hours credits nothing and returns {@code claimed: false}. */
export function claimDailyQuest(playerId: string): Promise<DailyQuestResponse> {
  return api<DailyQuestResponse>(`/players/${encodeURIComponent(playerId)}/wallet/daily-quest`, {
    method: 'POST',
  });
}

/** {@code GET /v1/players/{id}/wallet/weekly-gift}: whether this week's gift
 *  from the Facebook Page's links is still to open, and the prize table. */
export function fetchWeeklyGift(playerId: string): Promise<WeeklyGiftResponse> {
  return api<WeeklyGiftResponse>(`/players/${encodeURIComponent(playerId)}/wallet/weekly-gift`);
}

/** {@code POST /v1/players/{id}/wallet/weekly-gift}: open this week's gift;
 *  the server picks the prize. Safe to retry: a second claim in the same
 *  week credits nothing and returns the prize taken earlier. */
export function claimWeeklyGift(playerId: string): Promise<WeeklyGiftResponse> {
  return api<WeeklyGiftResponse>(`/players/${encodeURIComponent(playerId)}/wallet/weekly-gift`, {
    method: 'POST',
  });
}

/** {@code GET /v1/players/{id}/wallet/world-offer/{offerId}}: where the
 *  player stands with a world offer from a Facebook post. */
export function fetchWorldOffer(playerId: string, offerId: string): Promise<WorldOfferResponse> {
  return api<WorldOfferResponse>(
    `/players/${encodeURIComponent(playerId)}/wallet/world-offer/${encodeURIComponent(offerId)}`,
  );
}

/** {@code POST /v1/players/{id}/wallet/world-offer/{offerId}}: take an open
 *  world offer; its games join the unlocked slots. Safe to retry. */
export function claimWorldOffer(playerId: string, offerId: string): Promise<WorldOfferResponse> {
  return api<WorldOfferResponse>(
    `/players/${encodeURIComponent(playerId)}/wallet/world-offer/${encodeURIComponent(offerId)}`,
    { method: 'POST' },
  );
}
